import { c as _c } from "react/compiler-runtime";
import { fetchBoardDetail, fetchBoardList, fetchFaq, fetchInquireDelete, fetchInquireList, fetchInquireModify, fetchInquireWrite } from '@module/apis/client-side/customer';
import type { BoardListParams, BoardListResponse, FetchBoardDetailParams, FetchBoardDetailResponse, FetchFaqParams, FetchFaqResponse, FetchInquireDeleteParams, FetchInquireDeleteResponse, FetchInquireListParams, FetchInquireListResponse, FetchInquireModifyParams, FetchInquireWriteParams, FetchInquireWriteResponse } from '@module/apis/client-side/customer/type';
import type { ApiBaseResponse } from '@module/apis/type';
import { type UseInfiniteQueryOptions, type UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { customerQueryKeys } from './customerQueryKeys';
export const useFaqListQuery = (fetchFaqParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchFaqParams) {
    t0 = customerQueryKeys.fetchFaq(fetchFaqParams);
    $[0] = fetchFaqParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchFaqParams) {
    t1 = () => fetchFaq(fetchFaqParams);
    $[2] = fetchFaqParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFaqListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = customerQueryKeys.fetchFaq(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchFaq({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp2,
      select: _temp4
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useBoardListQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = customerQueryKeys.fetchBoardList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => fetchBoardList(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useBoardListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = customerQueryKeys.fetchBoardList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchBoardList({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      staleTime: TimeConstants.ONE_MINUTE,
      ...options,
      getNextPageParam: _temp6,
      select: _temp8
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useBoardDetailQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = customerQueryKeys.fetchBoardDetail(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => fetchBoardDetail(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useInquireListQuery = (fetchInquireListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== fetchInquireListParams) {
    t0 = customerQueryKeys.fetchInquireList(fetchInquireListParams);
    $[0] = fetchInquireListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== fetchInquireListParams) {
    t1 = () => fetchInquireList(fetchInquireListParams);
    $[2] = fetchInquireListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useInquireListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = customerQueryKeys.fetchInquireList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchInquireList({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp10,
      select: _temp12
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useInquireWriteMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = customerQueryKeys.fetchInquireWrite();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp13,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useInquireModifyMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = customerQueryKeys.fetchInquireModify();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp14,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useInquireDeleteMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = customerQueryKeys.fetchInquireDelete();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0.queryKey,
      mutationFn: _temp15,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
function _temp(page) {
  return page.list;
}
function _temp2(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp3(page_0) {
  return page_0.list ?? [];
}
function _temp4(data) {
  return {
    pages: data?.pages.flatMap(_temp3),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp5(page) {
  return page.list;
}
function _temp6(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp5).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp7(page_0) {
  return page_0.list ?? [];
}
function _temp8(data) {
  return {
    pages: data?.pages.flatMap(_temp7),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp9(page) {
  return page.list;
}
function _temp10(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp9).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp11(page_0) {
  return page_0.list ?? [];
}
function _temp12(data) {
  return {
    pages: data?.pages.flatMap(_temp11),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp13(inquireWirteParams) {
  return fetchInquireWrite(inquireWirteParams);
}
function _temp14(inquireModifyParams) {
  return fetchInquireModify(inquireModifyParams);
}
function _temp15(inquireWirteParams) {
  return fetchInquireDelete(inquireWirteParams);
}